import api from './config';

/**
 * API Client para módulo de Gestión de Ausencias
 */
export const absencesAPI = {
  // ============================================================================
  // CRUD Básico
  // ============================================================================

  /**
   * Obtener lista de ausencias con filtros y paginación
   * @param {Object} filters - Filtros y paginación
   * @returns {Promise} Lista de ausencias
   */
  getAll: async (filters = {}) => {
    const response = await api.get('/absences', { params: filters });
    return response.data;
  },

  /**
   * Obtener ausencia por ID
   * @param {string} id - ID de la ausencia
   * @returns {Promise} Datos de la ausencia
   */
  getById: async (id) => {
    const response = await api.get(`/absences/${id}`);
    return response.data;
  },

  /**
   * Crear nueva solicitud de ausencia
   * @param {Object} data - Datos de la ausencia
   * @param {Array} documentos - Archivos a adjuntar (opcional)
   * @returns {Promise} Ausencia creada
   */
  create: async (data, documentos = []) => {
    const formData = new FormData();

    // Adjuntar documentos si existen
    if (documentos && documentos.length > 0) {
      documentos.forEach(doc => {
        formData.append('documentos', doc);
      });
    }

    // Adjuntar datos JSON
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });

    const response = await api.post('/absences', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // ============================================================================
  // Aprobación y Gestión
  // ============================================================================

  /**
   * Aprobar ausencia (avanza al siguiente nivel de aprobación)
   * @param {string} id - ID de la ausencia
   * @param {string} comentarios - Comentarios del aprobador (opcional)
   * @returns {Promise} Resultado de la aprobación
   */
  aprobar: async (id, comentarios = '') => {
    const response = await api.post(`/absences/${id}/aprobar`, { comentarios });
    return response.data;
  },

  /**
   * Rechazar ausencia
   * @param {string} id - ID de la ausencia
   * @param {string} motivo - Motivo del rechazo (requerido)
   * @returns {Promise} Resultado del rechazo
   */
  rechazar: async (id, motivo) => {
    const response = await api.post(`/absences/${id}/rechazar`, { motivo });
    return response.data;
  },

  /**
   * Cancelar ausencia (por el solicitante)
   * @param {string} id - ID de la ausencia
   * @param {string} motivo - Motivo de la cancelación (opcional)
   * @returns {Promise} Resultado de la cancelación
   */
  cancelar: async (id, motivo = '') => {
    const response = await api.post(`/absences/${id}/cancelar`, { motivo });
    return response.data;
  },

  // ============================================================================
  // Tipos de Ausencias
  // ============================================================================

  /**
   * Obtener tipos de ausencias disponibles
   * @returns {Promise} Lista de tipos de ausencias
   */
  getTypes: async () => {
    const response = await api.get('/absences/types');
    return response.data;
  },

  // ============================================================================
  // Consultas y Estadísticas
  // ============================================================================

  /**
   * Obtener balance de días disponibles para un empleado
   * @param {string} employeeId - ID del empleado
   * @param {number} anio - Año (opcional, por defecto año actual)
   * @returns {Promise} Balance de días por tipo
   */
  getBalance: async (employeeId, anio = new Date().getFullYear()) => {
    const response = await api.get(`/absences/balance/${employeeId}`, { params: { anio } });
    return response.data;
  },

  /**
   * Obtener estadísticas de ausencias
   * @param {Object} filters - Filtros (year, month, departmentId)
   * @returns {Promise} Estadísticas agregadas
   */
  getStats: async (filters = {}) => {
    const response = await api.get('/absences/stats', { params: filters });
    return response.data;
  },

  /**
   * Obtener calendario de ausencias
   * @param {string} startDate - Fecha de inicio (YYYY-MM-DD)
   * @param {string} endDate - Fecha de fin (YYYY-MM-DD)
   * @param {Object} filters - Filtros adicionales (departmentId, employeeId)
   * @returns {Promise} Ausencias en el rango de fechas
   */
  getCalendar: async (startDate, endDate, filters = {}) => {
    const response = await api.get('/absences/calendar', {
      params: { startDate, endDate, ...filters }
    });
    return response.data;
  }
};

/**
 * Estados de ausencias con configuración
 */
export const ESTADOS_AUSENCIAS = {
  pendiente: {
    label: 'Pendiente',
    color: 'yellow',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-800',
    borderColor: 'border-yellow-300'
  },
  aprobado: {
    label: 'Aprobado',
    color: 'green',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    borderColor: 'border-green-300'
  },
  rechazado: {
    label: 'Rechazado',
    color: 'red',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
    borderColor: 'border-red-300'
  },
  cancelado: {
    label: 'Cancelado',
    color: 'gray',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800',
    borderColor: 'border-gray-300'
  },
  completado: {
    label: 'Completado',
    color: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
    borderColor: 'border-blue-300'
  }
};

/**
 * Categorías de ausencias con configuración
 */
export const CATEGORIAS_AUSENCIAS = {
  vacaciones: {
    label: 'Vacaciones',
    icon: '🌴',
    color: '#10B981'
  },
  permiso: {
    label: 'Permiso',
    icon: '📋',
    color: '#F59E0B'
  },
  licencia_medica: {
    label: 'Licencia Médica',
    icon: '🏥',
    color: '#EF4444'
  },
  falta: {
    label: 'Falta',
    icon: '❌',
    color: '#6B7280'
  },
  suspension: {
    label: 'Suspensión',
    icon: '⚠️',
    color: '#7C3AED'
  },
  ausencia_justificada: {
    label: 'Ausencia Justificada',
    icon: '✓',
    color: '#3B82F6'
  },
  ausencia_injustificada: {
    label: 'Ausencia Injustificada',
    icon: '✗',
    color: '#1F2937'
  }
};

/**
 * Helper para obtener configuración de estado
 * @param {string} estado - Estado de la ausencia
 * @returns {Object} Configuración del estado
 */
export const getEstadoConfig = (estado) => {
  return ESTADOS_AUSENCIAS[estado] || {
    label: estado,
    color: 'gray',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800',
    borderColor: 'border-gray-300'
  };
};

/**
 * Helper para obtener configuración de categoría
 * @param {string} categoria - Categoría de la ausencia
 * @returns {Object} Configuración de la categoría
 */
export const getCategoriaConfig = (categoria) => {
  return CATEGORIAS_AUSENCIAS[categoria] || {
    label: categoria,
    icon: '📄',
    color: '#6B7280'
  };
};

/**
 * Helper para formatear rango de fechas
 * @param {string} fechaInicio - Fecha de inicio
 * @param {string} fechaFin - Fecha de fin
 * @returns {string} Rango formateado
 */
export const formatearRangoFechas = (fechaInicio, fechaFin) => {
  // Forzar mediodía local para evitar el bug UTC-4 (new Date("YYYY-MM-DD") = medianoche UTC = día anterior)
  const inicio = new Date(String(fechaInicio).split('T')[0] + 'T12:00:00');
  const fin = new Date(String(fechaFin).split('T')[0] + 'T12:00:00');

  const opcionesCortas = { day: 'numeric', month: 'short' };
  const opcionesLargas = { day: 'numeric', month: 'short', year: 'numeric' };

  if (inicio.getFullYear() === fin.getFullYear()) {
    if (inicio.getMonth() === fin.getMonth() && inicio.getDate() === fin.getDate()) {
      // Mismo día
      return inicio.toLocaleDateString('es-ES', opcionesLargas);
    }
    // Mismo año
    return `${inicio.toLocaleDateString('es-ES', opcionesCortas)} - ${fin.toLocaleDateString('es-ES', opcionesLargas)}`;
  }

  // Años diferentes
  return `${inicio.toLocaleDateString('es-ES', opcionesLargas)} - ${fin.toLocaleDateString('es-ES', opcionesLargas)}`;
};

export default absencesAPI;
